/**
 * Type Detectors - Barrel Export
 * 
 * Central location for:
 * - Semantic type definitions
 * - Type detectors
 * - Detector registry
 * 
 * When adding a new semantic type:
 * 1. Add it to semanticTypes.ts
 * 2. Create a detector in impl/ folder (e.g., impl/MyTypeDetector.ts)
 * 3. Export it from impl/index.ts
 * 4. Register it below
 */

// Export semantic types (canonical source for values)
export { getSemanticTypeInfo, SEMANTIC_TYPES, type SemanticTypeKey } from './semanticTypes';

// Export registry
export { DetectorRegistry } from './registry';

// Export all detectors from impl/
export * from './impl';

// Register all detectors
import type { ISemanticType } from '@/interfaces/ISemanticType';

import {
  Base64Detector,
  HashDetector,
  HexDetector,
  HtmlDetector,
  JsonDetector,
  JwtDetector,
  MarkdownDetector,
  RegexDetector,
  SvgDetector,
  TabularDetector,
  TimestampDetector,
  TomlDetector,
  UrlDetector,
  UrlEncodedDetector,
  UuidDetector,
  XmlDetector,
  YamlDetector,
} from './impl';
import { DetectorRegistry } from './registry';

// Register detectors in priority order (highest first)
// Priority determines which detector "wins" when multiple could match
DetectorRegistry.register(JwtDetector);        // 100 - Most specific encoded format
DetectorRegistry.register(UuidDetector);       //  90 - Exact format
DetectorRegistry.register(TimestampDetector);  //  88 - Exact format patterns
DetectorRegistry.register(HashDetector);       //  85 - Exact lengths (MD5, SHA1, etc.)
DetectorRegistry.register(UrlDetector);        //  80 - Needs protocol
DetectorRegistry.register(RegexDetector);      //  78 - JS regex literal
DetectorRegistry.register(UrlEncodedDetector); //  75 - Percent encoding
DetectorRegistry.register(SvgDetector);        //  72 - Specific XML (SVG)
DetectorRegistry.register(HtmlDetector);       //  70 - Specific markup
DetectorRegistry.register(XmlDetector);        //  65 - Generic markup
DetectorRegistry.register(JsonDetector);       //  60 - Structured data
DetectorRegistry.register(YamlDetector);       //  55 - Structured data (harder to detect)
DetectorRegistry.register(TomlDetector);       //  52 - Structured data (needs section headers)
DetectorRegistry.register(Base64Detector);     //  50 - Encoded, needs validation
DetectorRegistry.register(HexDetector);        //  45 - Simple pattern
DetectorRegistry.register(MarkdownDetector);   //  40 - Text patterns
DetectorRegistry.register(TabularDetector);    //  35 - CSV/TSV/Pipe (below Markdown)

/**
 * Detect the primary type of the given input.
 */
export function detectType(input: string): ISemanticType | null {
  return DetectorRegistry.detect(input);
}

/**
 * Detect all possible types with their confidence scores.
 */
export function detectAll(input: string): Array<{ type: ISemanticType; confidence: number }> {
  return DetectorRegistry.detectAll(input);
}

